import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Button,
  Paper,
  Fade,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Select,
  MenuItem,
} from '@mui/material';
import { Send as SendIcon, Info as InfoIcon } from '@mui/icons-material';

interface BaseQuestion {
  id: string;
  question: string;
  question_type: string;
  options: string[] | null;
  multiple_selection: boolean;
  required: boolean;
  order: number;
  conditional?: string;
  help_text?: string;
}

interface BaseQuestionsFormProps {
  onSubmit: (answers: Record<string, any>) => Promise<void>;
  greeting?: string;
}

const BaseQuestionsForm: React.FC<BaseQuestionsFormProps> = ({ onSubmit, greeting }) => {
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customOtherText, setCustomOtherText] = useState<string>('');
  const [customPetType, setCustomPetType] = useState<string>('');
  const [ageValue, setAgeValue] = useState<string>('');
  const [ageUnit, setAgeUnit] = useState<string>('years');

  const questions: BaseQuestion[] = [
    {
      id: 'pet',
      question: 'What type of pet do you have?',
      question_type: 'pet',
      options: ['Dog', 'Cat', 'Other'],
      multiple_selection: false,
      required: true,
      order: 1
    },
    {
      id: 'pet_name',
      question: 'What is your pet\'s name?',
      question_type: 'pet_name',
      options: null,
      multiple_selection: false,
      required: true,
      order: 2
    },
    {
      id: 'age',
      question: 'How old is your pet?',
      question_type: 'age',
      options: null,
      multiple_selection: false,
      required: true,
      order: 3
    },
    {
      id: 'gender',
      question: 'Is your pet male or female?',
      question_type: 'gender',
      options: ['Male', 'Female'],
      multiple_selection: false,
      required: true,
      order: 4
    },
    {
      id: 'reproductive',
      question: 'Is your pet fixed?',
      question_type: 'reproductive',
      options: ['Fixed', 'Not fixed'],
      multiple_selection: false,
      required: true,
      order: 5,
      help_text: 'Fixed means neutered (for males) or spayed (for females)'
    },
    {
      id: 'symptoms',
      question: 'What changes have you noticed in your pet?',
      question_type: 'symptoms',
      options: [
        'Lethargy', 'Vomiting', 'Diarrhea', 'Coughing', 'Sneezing',
        'Increased appetite', 'Reduced appetite', 'Excessive thirst', 'Decreased thirst',
        'Weight loss', 'Weight gain', 'Excessive urination', 'Decreased urination', 'Bleeding', 'Weakness',
        'Pain', 'Lameness', 'Change in diet', 'Swelling', 'Blindness', 'Falling over',
        'Trauma', 'Other', 'None'
      ],
      multiple_selection: true,
      required: true,
      order: 6
    }
    // Duration question removed - will be asked in follow-up if needed based on case requirements
  ];

  const handleSingleChange = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setError(null);
    
    // Show custom input if "Other" is selected for pet type
    if (questionId === 'pet' && value !== 'Other') {
      setCustomPetType('');
    }
  };

  const handleMultipleChange = (questionId: string, option: string, checked: boolean) => {
    setAnswers(prev => {
      const currentValues = prev[questionId] || [];
      let newValues: string[];

      // Handle "None" selection
      if (option === 'None') {
        newValues = checked ? ['None'] : [];
      } else {
        if (checked) {
          // Remove "None" if present and add the new option
          newValues = [...currentValues.filter((v: string) => v !== 'None'), option];
        } else {
          newValues = currentValues.filter((v: string) => v !== option);
        }
      }

      return { ...prev, [questionId]: newValues };
    });
    setError(null);
  };

  const handleTextChange = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setError(null);
  };

  const shouldShowQuestion = (question: BaseQuestion): boolean => {
    if (!question.conditional) return true;
    
    // Check if the conditional field has symptoms (not "None")
    if (question.conditional === 'symptoms') {
      const symptoms = answers.symptoms || [];
      return symptoms.length > 0 && !symptoms.includes('None');
    }
    
    return true;
  };

  const validateForm = (): string | null => {
    for (const question of questions) {
      if (!shouldShowQuestion(question)) continue;
      
      if (question.required) {
        // Special handling for age field
        if (question.id === 'age') {
          if (!ageValue || ageValue.trim() === '') {
            return 'Please enter your pet\'s age';
          }
          const ageNum = parseFloat(ageValue);
          if (isNaN(ageNum) || ageNum <= 0) {
            return 'Please enter a valid age';
          }
        } else {
          const value = answers[question.id];
          if (!value || (Array.isArray(value) && value.length === 0) || value === '') {
            return `Please answer: ${question.question}`;
          }
          
          // Check if "Other" is selected for pet type but no custom text provided
          if (question.id === 'pet' && value === 'Other' && !customPetType.trim()) {
            return 'Please specify what type of pet you have';
          }
        }
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Replace "Other" with custom text if provided
      const finalAnswers = { ...answers };
      
      // Handle age field - convert to years with appropriate format
      const ageNum = parseFloat(ageValue);
      if (ageUnit === 'months') {
        // Convert months to years and format appropriately
        const years = ageNum / 12;
        if (ageNum < 12) {
          finalAnswers.age = `${ageNum} month${ageNum !== 1 ? 's' : ''}`;
        } else {
          finalAnswers.age = years.toFixed(1);
        }
      } else {
        // Already in years
        finalAnswers.age = ageNum.toString();
      }
      
      // Replace "Other" pet type with custom pet type
      if (finalAnswers.pet === 'Other' && customPetType.trim()) {
        finalAnswers.pet = customPetType.trim();
      }
      
      // Replace "Other" symptoms with custom text
      if (finalAnswers.symptoms && Array.isArray(finalAnswers.symptoms)) {
        if (finalAnswers.symptoms.includes('Other') && customOtherText.trim()) {
          finalAnswers.symptoms = finalAnswers.symptoms.filter(s => s !== 'Other');
          finalAnswers.symptoms.push(customOtherText.trim());
        }
      }

      await onSubmit(finalAnswers);
    } catch (err: any) {
      setError(err.message || 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question: BaseQuestion) => {
    if (!shouldShowQuestion(question)) return null;

    return (
      <Box key={question.id} sx={{ mb: 4 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ 
            mb: 2, 
            fontWeight: 600, 
            fontSize: '1rem',
            color: '#1976d2',
            '&.Mui-focused': {
              color: '#1976d2'
            }
          }}>
            {question.question}
            {question.required && <span style={{ color: '#d32f2f' }}> *</span>}
          </FormLabel>

          {question.help_text && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ fontSize: 16, color: '#666' }} />
              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                {question.help_text}
              </Typography>
            </Box>
          )}

          {/* Text Input */}
          {!question.options && question.id !== 'age' && (
            <TextField
              fullWidth
              value={answers[question.id] || ''}
              onChange={(e) => handleTextChange(question.id, e.target.value)}
              placeholder={`Enter ${question.question.toLowerCase()}`}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}

          {/* Age Input with Unit Selection */}
          {question.id === 'age' && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                type="text"
                value={ageValue}
                onChange={(e) => {
                  // Only allow numeric values and decimal point
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setAgeValue(value);
                    setError(null);
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent up/down arrow keys from any default behavior
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onWheel={(e) => {
                  // Prevent mouse wheel from changing value
                  e.currentTarget.blur();
                }}
                placeholder="Enter age"
                variant="outlined"
                inputProps={{ 
                  inputMode: 'decimal',
                  pattern: '[0-9]*\\.?[0-9]*'
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  value={ageUnit}
                  onChange={(e) => setAgeUnit(e.target.value)}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="months">Months</MenuItem>
                  <MenuItem value="years">Years</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Single Selection */}
          {question.options && !question.multiple_selection && (
            <RadioGroup
              value={answers[question.id] || ''}
              onChange={(e) => handleSingleChange(question.id, e.target.value)}
            >
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: 1
              }}>
                {question.options.map((option) => (
                  <Box key={option}>
                    <FormControlLabel
                      value={option}
                      control={
                        <Radio
                          sx={{
                            color: '#1976d2',
                            '&.Mui-checked': {
                              color: '#1976d2',
                            }
                          }}
                        />
                      }
                      label={option}
                      sx={{
                        m: 0,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: answers[question.id] === option ? '#1976d2' : '#e0e0e0',
                        bgcolor: answers[question.id] === option ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#1976d2',
                          bgcolor: 'rgba(25, 118, 210, 0.04)',
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </RadioGroup>
          )}

          {/* Custom "Other" input for pet type */}
          {question.id === 'pet' && answers.pet === 'Other' && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Please specify pet type"
                value={customPetType}
                onChange={(e) => setCustomPetType(e.target.value)}
                placeholder="E.g., Hamster, Guinea Pig, Ferret, Lizard..."
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>
          )}

          {/* Multiple Selection */}
          {question.options && question.multiple_selection && (
            <Box>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: 1
              }}>
                {question.options.map((option) => (
                  <Box key={option}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={answers[question.id]?.includes(option) || false}
                          onChange={(e) => handleMultipleChange(question.id, option, e.target.checked)}
                          sx={{
                            color: '#1976d2',
                            '&.Mui-checked': {
                              color: '#1976d2',
                            }
                          }}
                        />
                      }
                      label={option}
                      sx={{
                        m: 0,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: answers[question.id]?.includes(option) ? '#1976d2' : '#e0e0e0',
                        bgcolor: answers[question.id]?.includes(option) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#1976d2',
                          bgcolor: 'rgba(25, 118, 210, 0.04)',
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Custom "Other" input for symptoms */}
              {question.id === 'symptoms' && answers.symptoms?.includes('Other') && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Please specify other symptoms"
                    value={customOtherText}
                    onChange={(e) => setCustomOtherText(e.target.value)}
                    placeholder="Describe what you've noticed..."
                    multiline
                    rows={2}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Box>
              )}

              {/* Show selected items */}
              {answers[question.id] && answers[question.id].length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                    Selected:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {answers[question.id].map((option: string) => (
                      <Chip
                        key={option}
                        label={option}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onDelete={() => handleMultipleChange(question.id, option, false)}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </FormControl>
      </Box>
    );
  };

  return (
    <Fade in={true} timeout={500}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 900,
          mx: 'auto',
          borderRadius: 3,
          background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
        }}
      >
        {/* Greeting */}
        {greeting && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#1976d2' }}>
              Welcome! 👋
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
              {greeting}
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 4 }} />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Questions */}
        {questions.sort((a, b) => a.order - b.order).map(renderQuestion)}

        {/* Submit Button */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              bgcolor: '#1976d2',
              minWidth: '200px',
              height: '50px',
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                bgcolor: '#1565c0',
                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                transform: 'translateY(-2px)',
              },
              '&.Mui-disabled': {
                bgcolor: '#e0e0e0',
                color: '#9e9e9e',
                boxShadow: 'none',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit & Start Diagnosis'}
          </Button>
        </Box>

        {/* Required fields note */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#666' }}>
            <span style={{ color: '#d32f2f' }}>*</span> Required fields
          </Typography>
        </Box>
      </Paper>
    </Fade>
  );
};

export default BaseQuestionsForm;

